import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { getBillFilterHeader, filterBillsList, getCreditCustomers, formatDateForApi } from '../../utilities/api';

// Async thunk to fetch filter options (branches)
export const fetchFilterOptions = createAsyncThunk('creditReports/fetchFilterOptions', async ({ rolename, clientname }, { rejectWithValue }) => {
    try {
        const response = await getBillFilterHeader({ rolename, clientname });
        return response;
    } catch (error) {
        return rejectWithValue(error.message || 'Failed to fetch filter options');
    }
});

// Async thunk to fetch distinct customer names (cust_name) for CREDIT bills
export const fetchCustomers = createAsyncThunk('creditReports/fetchCustomers', async ({ branchId, clientId }, { rejectWithValue }) => {
    try {
        const response = await getCreditCustomers({ branch_id: branchId || '', client_id: clientId || '' });
        return response;
    } catch (error) {
        return rejectWithValue(error.message || 'Failed to fetch customers');
    }
});

// Async thunk to fetch credit bills data (mode of payment is always CREDIT)
export const fetchCreditBillsData = createAsyncThunk('creditReports/fetchCreditBillsData', async ({ fromDate, toDate, branchId, custName }, { rejectWithValue }) => {
    try {
        const resolvedBranchId = branchId || '';
        const payload = {
            from_date: fromDate ? formatDateForApi(fromDate) : '',
            to_date: toDate ? formatDateForApi(toDate) : '',
            branch_id: resolvedBranchId,
            mop: 'CREDIT',
            cust_name: custName || ''
        };
        if (String(resolvedBranchId).toLowerCase() === 'all') {
            const clientId = localStorage.getItem('clientid');
            if (clientId) payload.client_id = clientId;
        }
        const data = await filterBillsList(payload);
        return data;
    } catch (error) {
        return rejectWithValue(error.message || 'Failed to fetch credit bills data');
    }
});

const initialState = {
    // Filter options
    branches: [],
    customers: [],

    // Selected filters
    selectedBranch: null,
    selectedCustomer: null,
    fromDate: null,
    toDate: null,

    // Bill data
    billData: [],

    // Loading states
    loading: false,
    filterOptionsLoading: false,
    customersLoading: false,

    // Error states
    error: null,
    filterOptionsError: null,
    customersError: null
};

const creditReportsSlice = createSlice({
    name: 'creditReports',
    initialState,
    reducers: {
        setFromDate: (state, action) => {
            state.fromDate = action.payload;
        },
        setToDate: (state, action) => {
            state.toDate = action.payload;
        },
        setSelectedBranch: (state, action) => {
            state.selectedBranch = action.payload;
            state.selectedCustomer = null;
            state.customers = [];
        },
        setSelectedCustomer: (state, action) => {
            state.selectedCustomer = action.payload;
        },
        setFilters: (state, action) => {
            const { fromDate, toDate, selectedBranch, selectedCustomer } = action.payload;
            if (fromDate !== undefined) state.fromDate = fromDate;
            if (toDate !== undefined) state.toDate = toDate;
            if (selectedBranch !== undefined) state.selectedBranch = selectedBranch;
            if (selectedCustomer !== undefined) state.selectedCustomer = selectedCustomer;
        },
        resetFilters: (state) => {
            state.fromDate = null;
            state.toDate = null;
            state.selectedBranch = null;
            state.selectedCustomer = null;
            state.customers = [];
            state.billData = [];
            state.error = null;
        },
        clearBillData: (state) => {
            state.billData = [];
        },
        clearError: (state) => {
            state.error = null;
            state.filterOptionsError = null;
            state.customersError = null;
        }
    },
    extraReducers: (builder) => {
        // Fetch filter options
        builder
            .addCase(fetchFilterOptions.pending, (state) => {
                state.filterOptionsLoading = true;
                state.filterOptionsError = null;
            })
            .addCase(fetchFilterOptions.fulfilled, (state, action) => {
                state.filterOptionsLoading = false;
                const rawBranches = Array.isArray(action.payload.branchs) ? action.payload.branchs : [];
                const hasAll = rawBranches.some((b) => String(b?.branch_id).toLowerCase() === 'all');
                state.branches = hasAll ? rawBranches : [{ branch_id: 'All', branch_name: 'All Branches' }, ...rawBranches];
            })
            .addCase(fetchFilterOptions.rejected, (state, action) => {
                state.filterOptionsLoading = false;
                state.filterOptionsError = action.payload;
            });

        // Fetch customers
        builder
            .addCase(fetchCustomers.pending, (state) => {
                state.customersLoading = true;
                state.customersError = null;
            })
            .addCase(fetchCustomers.fulfilled, (state, action) => {
                state.customersLoading = false;
                const rawCustomers = Array.isArray(action.payload) ? action.payload : [];
                state.customers = rawCustomers.map((c) => c.cust_name).filter(Boolean);
            })
            .addCase(fetchCustomers.rejected, (state, action) => {
                state.customersLoading = false;
                state.customersError = action.payload;
                state.customers = [];
            });

        // Fetch credit bills data
        builder
            .addCase(fetchCreditBillsData.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.billData = [];
            })
            .addCase(fetchCreditBillsData.fulfilled, (state, action) => {
                state.loading = false;
                state.billData = action.payload;
            })
            .addCase(fetchCreditBillsData.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
                state.billData = [];
            });
    }
});

// Export actions
export const { setFromDate, setToDate, setSelectedBranch, setSelectedCustomer, setFilters, resetFilters, clearBillData, clearError } = creditReportsSlice.actions;

// Selectors
export const selectCreditReports = (state) => state.creditReports;
export const selectBillData = (state) => state.creditReports.billData;
export const selectLoading = (state) => state.creditReports.loading;
export const selectBranches = (state) => state.creditReports.branches;
export const selectCustomers = (state) => state.creditReports.customers;
export const selectCustomersLoading = (state) => state.creditReports.customersLoading;
export const selectSelectedBranch = (state) => state.creditReports.selectedBranch;
export const selectSelectedCustomer = (state) => state.creditReports.selectedCustomer;
export const selectFromDate = (state) => state.creditReports.fromDate;
export const selectToDate = (state) => state.creditReports.toDate;
export const selectError = (state) => state.creditReports.error;

export default creditReportsSlice.reducer;
